import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type { DashboardPeriod } from './finance.api'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface ProviderUtilizationQueryParams {
  clinicId: string
  date: string
  period: DashboardPeriod
}

export interface ProviderUtilizationOverviewRow {
  id: string
  name: string
  utilization: string
  idleTime: string
  bookedTime: string
}

export interface ProviderUtilizationOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  rows: ProviderUtilizationOverviewRow[]
}

export interface ProviderUtilizationKpiData {
  avgUtilization: string
  avgUtilizationGoal: string | null
  totalBookedHours: string
  totalIdleHours: string
  scheduledToday: string
  topProvider: string
  topProviderUtilization: string
  comparisonLabel: string
  utilizationDelta: string
  utilizationTrend: 'up' | 'down'
  bookedDelta: string
  bookedTrend: 'up' | 'down'
  idleDelta: string
  idleTrend: 'up' | 'down'
}

export interface ProviderUtilizationChartPoint {
  label: string
  booked: number
  idle: number
}

export interface ProviderUtilizationChartConfig {
  data: ProviderUtilizationChartPoint[]
  xAxisDomain: [number, number]
  xAxisTicks: number[]
}

export interface ProviderUtilizationByProviderRow {
  id: string
  provider: string
  role: string
  utilization: string
  bookedHours: string
  idleHours: string
  availableHours: string
  appointments: number
  avgApptDuration: string
}

export interface ProviderUtilizationByTimePeriodRow {
  id: string
  timePeriod: string
  avgUtilization: string
  totalBookedHours: string
  totalIdleHours: string
  totalAppointments: number
}

export interface ProviderUtilizationDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: ProviderUtilizationKpiData
  chart: ProviderUtilizationChartConfig
  byProvider: ProviderUtilizationByProviderRow[]
  byTimePeriod: ProviderUtilizationByTimePeriodRow[]
}

const providerUtilizationGet = <T>(
  path: string,
  params: ProviderUtilizationQueryParams,
  options?: { signal?: AbortSignal },
): Promise<T> =>
  axios
    .get<
      ApiResponse<T>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/scheduling/provider-utilization/${path}`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getProviderUtilizationOverview = (
  params: ProviderUtilizationQueryParams,
  options?: { signal?: AbortSignal },
): Promise<ProviderUtilizationOverviewResult> =>
  providerUtilizationGet('overview', params, options)

export const getProviderUtilizationDetail = (
  params: ProviderUtilizationQueryParams,
  options?: { signal?: AbortSignal },
): Promise<ProviderUtilizationDetailResult> =>
  providerUtilizationGet('detail', params, options)
